"use client";

import { useEffect, useRef, useState } from "react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useCopilotStore } from "@/app/lib/stores/useCopilotStore";
import { useCopilotCacheInvalidation } from "@/app/lib/hooks/useCopilotCacheInvalidation";
import { ChatMessage } from "./copilot/ChatMessage";
import { ConfirmationRequest } from "./copilot/ConfirmationRequest";
import { ToolExecutionStatus } from "./copilot/ToolExecutionStatus";
import { MessageInput } from "./copilot/MessageInput";
import { TypingIndicator } from "./copilot/TypingIndicator";
import { ConnectionStatus } from "./copilot/ConnectionStatus";
import { DragHandle } from "./copilot/DragHandle";
import { cn } from "@/app/lib/utils";

export function CopilotPanel() {
  const {
    isOpen,
    panelWidth,
    messages,
    confirmationRequests,
    toolExecutions,
    isConnected,
    isConnecting,
    isTyping,
    connectionError,
    closePanel,
    setPanelWidth,
    sendMessage,
    handleConfirmation,
    startNewChat,
  } = useCopilotStore();

  // Enable cache invalidation handling
  useCopilotCacheInvalidation();

  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidth, setDragStartWidth] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Handle mouse drag for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartWidth(panelWidth);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = dragStartX - e.clientX;
      const newWidth = dragStartWidth + deltaX;
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setPanelWidth(newWidth);
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartWidth, setPanelWidth]);

  // Touch support for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragStartWidth(panelWidth);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = dragStartX - touch.clientX;
      const newWidth = dragStartWidth + deltaX;
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setPanelWidth(newWidth);
      });
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, dragStartX, dragStartWidth, setPanelWidth]);

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={closePanel}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          "bg-white flex flex-col flex-shrink-0 overflow-x-hidden",
          "border-l border-gray-200 h-full relative",
          !isOpen && "hidden",
          !isDragging && "transition-all duration-300"
        )}
        style={{ width: `${panelWidth}px` }}
      >
        {/* Drag handle */}
        <DragHandle 
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-medium">Sotro Copilot</h2>
            <span className="text-xs px-1 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-normal">
              beta
            </span>
            <ConnectionStatus
              isConnected={isConnected}
              isConnecting={isConnecting}
              error={connectionError}
            />
          </div>
          <div className="flex items-center gap-2">
            {/* New chat button - only show when there are messages */}
            {messages.length > 0 && (
              <button
                onClick={startNewChat}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors group"
                aria-label="Start new chat"
                title="Start new chat"
              >
                <PlusIcon className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
              </button>
            )}
            <button
              onClick={closePanel}
              className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Close Sotro Copilot panel"
            >
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Messages container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 copilot-messages">
          {messages.length === 0 && !isConnecting && (
            <div className="text-center text-gray-500 mt-8">
              <p className="text-sm">Welcome to Sotro Copilot</p>
              <p className="text-sm mt-2 text-gray-400">How can I help you today?</p>
            </div>
          )}

          {/* Render messages and interleave tool executions and confirmations */}
          {(() => {
            const items: Array<{
              type: 'message' | 'tool' | 'confirmation';
              data: any;
              timestamp: Date;
              key: string;
            }> = [];

            // Add messages
            messages.forEach((msg) => {
              items.push({
                type: 'message',
                data: msg,
                timestamp: msg.timestamp,
                key: msg.id,
              });
            });

            // Add confirmation requests
            Array.from(confirmationRequests.values()).forEach((request) => {
              items.push({
                type: 'confirmation',
                data: request,
                timestamp: request.timestamp,
                key: `conf-${request.id}`,
              });
            });

            // Add tool executions (filtered)
            const isDebugMode = process.env.NEXT_PUBLIC_DEBUG_MODE === 'true';
            Array.from(toolExecutions.values())
              .filter(exec => {
                // Don't show tool executions that require confirmation
                if (exec.requires_confirmation) return false;
                // Hide database_query tool unless in debug mode
                if (!isDebugMode && exec.tool_name === 'database_query') return false;
                return true;
              })
              .forEach((execution) => {
                items.push({
                  type: 'tool',
                  data: execution,
                  timestamp: execution.timestamp,
                  key: `tool-${execution.tool_call_id}`,
                });
              });

            // Sort all items by timestamp
            items.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

            // Render sorted items
            return items.map((item) => {
              switch (item.type) {
                case 'message':
                  return <ChatMessage key={item.key} message={item.data} />;
                case 'confirmation':
                  return (
                    <ConfirmationRequest
                      key={item.key}
                      request={item.data}
                      onApprove={(reason, data) =>
                        handleConfirmation(item.data.id, true, reason, data)
                      }
                      onDeny={(reason) => 
                        handleConfirmation(item.data.id, false, reason)
                      }
                    />
                  );
                case 'tool':
                  return <ToolExecutionStatus key={item.key} execution={item.data} />;
                default:
                  return null;
              }
            });
          })()}

          {isTyping && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <MessageInput
          onSendMessage={sendMessage}
        />
      </div>
    </>
  );
}
